import React from "react";
import { Message as MessageType, UserI, messageType, groupType } from "../../../../typings/interfaces";
import styles from "./message.scss"
import { formatDate, parseMessageContent, normalizeUrl } from "../../../../services";
import config from "appConfig";
import { getIconUrl } from "../../../../services/asset";
import { getDocumentIcon } from "../../../../utils/documentIcons";
import { ChatImageGallery } from "./ChatImageGallery";
import { WarningOutlined } from "@ant-design/icons";

const FINANCIAL_INFO_KEYWORDS = ["dpd", "oib", "verifikacij", "gls"];

type MessageProps = {
  message?: MessageType;
  messages?: MessageType[];
  groupType: typeof groupType.Single | typeof groupType.ImageGroup;
  user: UserI;
  isReadByOtherUser: boolean;
}

export const Message = ({ message, messages, groupType: groupTypeProp, user, isReadByOtherUser }: MessageProps) => {
  const isOwnMessage = groupTypeProp === groupType.ImageGroup
    ? messages[0].senderId === user.userId
    : message.senderId === user.userId;

  const renderTextWithLinks = (content: string) => {
    const { parts } = parseMessageContent(content);

    return (
      <span>
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return <React.Fragment key={index}>{part.content}</React.Fragment>;
          }

          // For links
          if (part.isExternal) {
            // External links are not clickable - shown as plain text
            return <React.Fragment key={index}>{part.content}</React.Fragment>;
          }

          // Internal links (index.hr) are clickable
          return (
            <a
              key={index}
              href={normalizeUrl(part.content)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.internalLink}
            >
              {part.content}
            </a>
          );
        })}
      </span>
    );
  };

  const hasExternalLinks = message && message.type === messageType.Text
    ? parseMessageContent(`${message.content}`).hasExternalLinks
    : false;

  const hasFinancialInfoKeywords = message && message.type === messageType.Text
    ? FINANCIAL_INFO_KEYWORDS.some(keyword => `${message.content}`.toLowerCase().includes(keyword))
    : false;

  const renderMessageContent = () => {
    if (React.isValidElement(message.content)) { // if message.content is a React Node
      return message.content
    } else if (message.type === messageType.Text) {
      return renderTextWithLinks(`${message.content}`)
    } else if (message.type === messageType.PDF) {
      return (
        <a
          href={`${config.baseFolder}${config.restEndpoint}/chat-document/${message.content}`}
          download
          className={styles.documentLink}
        >
          {getDocumentIcon({ fileName: message.fileName, size: 24 })}
          <span className={styles.documentName}>
            {message.fileName || 'Preuzmi dokument'}
          </span>
        </a>

      )
    }
  };

  const shouldShowExternalLinksSecurityWarning = hasExternalLinks && !isOwnMessage
  const shouldShowFinancialInfoSecurityWarning = hasFinancialInfoKeywords && !isOwnMessage && !shouldShowExternalLinksSecurityWarning
  const shouldShowSecurityWarning = [shouldShowExternalLinksSecurityWarning, shouldShowFinancialInfoSecurityWarning].filter(Boolean).length > 0

  return (
    <React.Fragment>
      {groupTypeProp === groupType.ImageGroup ? (
        <div className={`${styles.messageWrapper} ${styles.imageGroupWrapper} ${isOwnMessage ? styles.justifyEnd : styles.justifyStart}`}>
          <div className={styles.imageGroupContainer}>
            <ChatImageGallery
              messages={messages}
              isOwnMessage={isOwnMessage}
            />
            <div className={`${styles.timeStamp} ${styles.imageGroupTimeStamp} ${isOwnMessage ? styles.alignRight : styles.alignLeft}`}>
              {formatDate(messages[messages.length - 1].sentAt, "HH:mm")}
              {isOwnMessage && isReadByOtherUser && (
                <img alt="" src={getIconUrl("eye.svg")} />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`${styles.messageWrapper} ${isOwnMessage ? styles.justifyEnd : styles.justifyStart}`}>
          <div className={styles.messageContainer}>
            {message.type === messageType.PDF ? (
              // Document messages don't get bubble styling
              renderMessageContent()
            ) : (
              // Text and image messages get bubble styling
              <div
                className={`${styles.messageContent} ${isOwnMessage ? styles.ownMessage : styles.otherMessage} ${message.type !== messageType.Text ? styles.fileMessage : ''
                  }`}
              >
                {renderMessageContent()}
                {shouldShowSecurityWarning && (
                  <div className={styles.securityWarning}>
                    <div className={styles.warningContent}>
                      <WarningOutlined className={styles.warningIcon} />
                      <span className={styles.warningTitle}>Sigurnosno upozorenje</span>
                    </div>
                    {shouldShowExternalLinksSecurityWarning &&
                      <span className={styles.warningText}>Vanjska poveznica. Ne dijelite osobne ni financijske podatke.</span>}

                    {shouldShowFinancialInfoSecurityWarning &&
                      <span className={styles.warningText}><strong>Budite oprezni.</strong>{" "}Ne dijelite osobne ni financijske podatke dok niste sigurni s kim razgovarate.</span>}
                  </div>
                )}
              </div>
            )}
            <div className={`${styles.timeStamp} ${isOwnMessage ? styles.alignRight : styles.alignLeft}`}>
              {formatDate(message.sentAt, "HH:mm")}
              {isOwnMessage && isReadByOtherUser && (
                <img alt="" src={getIconUrl("eye.svg")} />
              )}
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/Message/Message.tsx